import { flushSync } from 'react-dom';

import { logger } from '@/logger';

// The single shared-element name the morph tweens. The dock bar carries it at rest and the
// MENU surface carries it while open, so the browser tweens ONE box growing out of / shrinking
// back into the dock (position/size/radius) while the content cross-fades — no stretched-
// screenshot warp. Exactly one live node may carry a given name per captured frame, so the
// resting bar drops the name while the menu holds it.
//
// Wrapping the close in a View Transition is also the ONLY way to animate it: React unmounts
// the menu synchronously, so a CSS exit animation on the removed node never runs (that is why
// the old `animate-dock-takeover-out` utility was dead code). The VT snapshots the old frame
// and tweens it back to the bar after the DOM has already changed.
export const DOCK_FRAME_TRANSITION_NAME = 'strategy-dock-frame';

// Set on <html> for the life of a dock morph. The global `::view-transition-*(root)` runs at
// 1000ms for the onboarding ceremony, and while it runs the root snapshot is an overlay that
// hit-tests as <html> — so clicks and scrolls anywhere in the app are swallowed for ~1.1s,
// long after the dock's own 260ms frame has visibly settled. A dock morph never changes the
// page behind the dock, so this class collapses root to the dock's cadence.
export const DOCK_MORPH_ROOT_CLASS = 'dock-morph';

// Which visual path a morph attempt actually took. Surfaced on the frame node via
// data-dock-anim so an e2e can assert 'morph' deterministically (no screenshot verdict).
export type DockAnimKind = 'morph' | 'grow' | 'instant';

// How many dock morphs are currently holding DOCK_MORPH_ROOT_CLASS on <html>. Morphs can
// overlap (a second one skips the first), and the class is one shared token.
let liveMorphs = 0;

function prefersReducedMotion(): boolean {
	return (
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches
	);
}

function supportsViewTransition(): boolean {
	return (
		typeof document !== 'undefined' &&
		typeof (document as Document & { startViewTransition?: unknown })
			.startViewTransition === 'function'
	);
}

// Decide the path for the CURRENT environment. Pure, so it is unit-testable.
export function resolveDockAnimKind(env: {
	supportsVT: boolean;
	reduceMotion: boolean;
}): DockAnimKind {
	if (env.reduceMotion) return 'instant';
	if (env.supportsVT) return 'morph';
	return 'grow';
}

// Run the open/close state swap with the best available motion:
//  - morph:   native View Transition tweens the shared frame (the premium path)
//  - grow:    no VT support but motion OK → the swap happens, CSS transitions the frame
//             (a real animated grow, never an instant jump)
//  - instant: prefers-reduced-motion → resolve straight to the final state
// `swap` must synchronously apply the React state change (open/close). Returns the kind
// taken so the caller can mark the frame node for deterministic assertions.
export function runDockMorph(swap: () => void): DockAnimKind {
	const kind = resolveDockAnimKind({
		supportsVT: supportsViewTransition(),
		reduceMotion: prefersReducedMotion(),
	});

	if (kind !== 'morph') {
		// grow + instant: apply the swap directly. For 'grow' the frame's own CSS
		// transition animates the box; for 'instant' the reduced-motion CSS resolves to
		// the final state with no transition.
		swap();
		return kind;
	}

	const root = document.documentElement;
	liveMorphs += 1;
	root.classList.add(DOCK_MORPH_ROOT_CLASS);
	// Refcounted, because starting a morph while one is live SKIPS the first, rejecting its
	// `finished`. A plain remove there would strip the class out from under the second morph —
	// which is still running — dropping it back to the 1000ms global and re-creating the very
	// input-swallow this class exists to prevent. Only the last morph standing releases.
	const release = () => {
		liveMorphs -= 1;
		if (liveMorphs === 0) root.classList.remove(DOCK_MORPH_ROOT_CLASS);
	};

	try {
		const transition = (
			document as Document & {
				startViewTransition: (cb: () => void) => {
					finished: Promise<void>;
				};
			}
		).startViewTransition(() => {
			flushSync(swap);
		});
		// then(release, release), not finally: finished REJECTS when a morph is skipped or
		// aborted, and the count must be decremented on both outcomes (a stuck class would
		// shorten the next onboarding ceremony) while the rejection is consumed so it is never
		// left unhandled (mirrors App.tsx).
		transition.finished.then(release, release);
	} catch (error) {
		logger.error('Strategy dock view transition failed', { error });
		release();
		swap();
	}
	return kind;
}
